// Staff Lead Generator roster hooks. Every mutation invalidates the
// `leadGeneratorKeys.all` prefix so the list + stats + detail reads refetch.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  leadGeneratorsApi,
  type LeadGeneratorListQuery,
  type LeadGeneratorStatus,
  type UpsertLeadGeneratorRequest,
} from "@/api/leadGen/leadGenerators";
import { leadGeneratorKeys } from "@/hooks/leadGen/leadGeneratorKeys";

export function useLeadGenerators(query: LeadGeneratorListQuery) {
  return useQuery({
    queryKey: leadGeneratorKeys.list(query as Record<string, unknown>),
    queryFn: () => leadGeneratorsApi.list(query),
  });
}

export function useLeadGeneratorStats(query: LeadGeneratorListQuery) {
  return useQuery({
    queryKey: leadGeneratorKeys.stats(query as Record<string, unknown>),
    queryFn: () => leadGeneratorsApi.stats(query),
  });
}

export function useLeadGenerator(id: string | null) {
  return useQuery({
    queryKey: leadGeneratorKeys.detail(id ?? ""),
    queryFn: () => leadGeneratorsApi.get(id as string),
    enabled: !!id,
  });
}

export function useCreateLeadGenerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertLeadGeneratorRequest) => leadGeneratorsApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadGeneratorKeys.all });
    },
  });
}

export function useUpdateLeadGenerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: UpsertLeadGeneratorRequest }) =>
      leadGeneratorsApi.update(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadGeneratorKeys.all });
    },
  });
}

export function useSetLeadGeneratorStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; status: LeadGeneratorStatus }) =>
      leadGeneratorsApi.setStatus(vars.id, vars.status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadGeneratorKeys.all });
    },
  });
}

export function useArchiveLeadGenerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; archived: boolean }) =>
      vars.archived
        ? leadGeneratorsApi.unarchive(vars.id)
        : leadGeneratorsApi.archive(vars.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadGeneratorKeys.all });
    },
  });
}

export function useInviteLeadGenerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => leadGeneratorsApi.portalInvite(id),
    onSuccess: () => {
      // A first invite flips Invited/portalUserId, so refresh the roster reads.
      void qc.invalidateQueries({ queryKey: leadGeneratorKeys.all });
    },
  });
}
